import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Users,
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  CreditCard,
} from "lucide-react";
import { Pagination, pageSlice } from "../components/Pagination";
import {
  useClientes,
  useCreateCliente,
  useUpdateCliente,
  useDeleteCliente,
} from "../api/clientes";
import { useIssuers } from "../api/issuers";
import type { Cliente, ClienteFormData } from "../types/cliente";
import { FormasPagoManager } from "../components/FormasPagoManager";
import { errMsg } from "@shared/api/errors";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";

const EMPTY_FORM: ClienteFormData = {
  nombre: "",
  cif: "",
  email: "",
  telefono: "",
  direccion: "",
  notas: "",
  emisor_id: 0,
};

export function ClientesListPage() {
  const [filterEmisor, setFilterEmisor] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState("");
  const { data: clientes, isLoading, error } = useClientes(filterEmisor, search);
  const { data: emisores } = useIssuers();

  const createMut = useCreateCliente();
  const updateMut = useUpdateCliente();
  const deleteMut = useDeleteCliente();

  const [editing, setEditing] = useState<Cliente | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<ClienteFormData>(EMPTY_FORM);
  const [managingFormasPago, setManagingFormasPago] = useState<Cliente | null>(
    null,
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<Cliente | null>(null);

  // ── Paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => {
    setPage(1);
  }, [filterEmisor, search]);
  const pagedClientes = useMemo(
    () => pageSlice(clientes, page, pageSize),
    [clientes, page, pageSize],
  );

  function openCreate() {
    setCreating(true);
    setEditing(null);
    setForm({ ...EMPTY_FORM, emisor_id: filterEmisor ?? emisores?.[0]?.id ?? 0 });
  }
  function openEdit(c: Cliente) {
    setEditing(c);
    setCreating(false);
    setForm({
      nombre: c.nombre,
      cif: c.cif ?? "",
      email: c.email ?? "",
      telefono: c.telefono ?? "",
      direccion: c.direccion ?? "",
      notas: c.notas ?? "",
      emisor_id: c.emisor_id ?? 0,
      cuenta_contable: c.cuenta_contable ?? "",
    });
  }
  function close() {
    setEditing(null);
    setCreating(false);
    setFormError(null);
  }
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, payload: form });
      } else {
        await createMut.mutateAsync(form);
      }
      close();
    } catch (err) {
      setFormError(errMsg(err, "No se pudo guardar el cliente."));
    }
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <Users size={28} className="text-brand-700" /> Clientes
          </h1>
          <p className="text-slate-500 mt-1">
            Cartera de clientes facturables por emisor.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 font-bold text-sm shadow-soft transition"
        >
          <Plus size={18} /> Nuevo cliente
        </button>
      </header>

      <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-4 grid grid-cols-1 md:grid-cols-3 gap-3 shrink-0">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Emisor
          </label>
          <select
            value={filterEmisor ?? ""}
            onChange={(e) =>
              setFilterEmisor(e.target.value ? Number(e.target.value) : undefined)
            }
            className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
          >
            <option value="">Todos</option>
            {emisores?.map((em) => (
              <option key={em.id} value={em.id}>
                {em.nombre}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Buscar (nombre, CIF, email)
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Búsqueda…"
            className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-4">
          Error al cargar.
        </div>
      )}

      {clientes && (
        <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden flex flex-col">
          <div className="overflow-auto max-h-[calc(100vh-300px)]">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Nombre</th>
                  <th className="text-left px-4 py-3 font-bold">CIF</th>
                  <th className="text-left px-4 py-3 font-bold">Email</th>
                  <th className="text-left px-4 py-3 font-bold">Teléfono</th>
                  <th className="text-right px-4 py-3 font-bold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clientes.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                      No hay clientes.
                    </td>
                  </tr>
                )}
                {pagedClientes.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-bold text-slate-900">{c.nombre}</td>
                  <td className="px-4 py-3 font-mono text-sm">{c.cif ?? "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{c.email ?? "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{c.telefono ?? "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        title="Formas de pago"
                        onClick={() => setManagingFormasPago(c)}
                        className="p-1.5 rounded hover:bg-sky-50 text-sky-700"
                      >
                        <CreditCard size={16} />
                      </button>
                      <button
                        title="Editar"
                        onClick={() => openEdit(c)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        title="Eliminar"
                        onClick={() => setConfirmDel(c)}
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={clientes.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      )}

      {managingFormasPago && (
        <FormasPagoManager
          clienteId={managingFormasPago.id}
          clienteNombre={managingFormasPago.nombre}
          onClose={() => setManagingFormasPago(null)}
        />
      )}

      {(creating || editing) && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form
            onSubmit={save}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6"
          >
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">
                {editing ? "Editar cliente" : "Nuevo cliente"}
              </h2>
              <button type="button" onClick={close} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>
            {formError && (
              <div className="mb-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-sm">
                {formError}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Emisor *
                </label>
                <select
                  required
                  value={form.emisor_id}
                  onChange={(e) =>
                    setForm({ ...form, emisor_id: Number(e.target.value) })
                  }
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value={0}>— elegir —</option>
                  {emisores?.map((em) => (
                    <option key={em.id} value={em.id}>
                      {em.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Nombre *
                </label>
                <input
                  required
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">CIF</label>
                <input
                  type="text"
                  value={form.cif ?? ""}
                  onChange={(e) => setForm({ ...form, cif: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Email</label>
                <input
                  type="email"
                  value={form.email ?? ""}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Teléfono</label>
                <input
                  type="text"
                  value={form.telefono ?? ""}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Cuenta contable</label>
                <input
                  type="text"
                  value={form.cuenta_contable ?? ""}
                  onChange={(e) => setForm({ ...form, cuenta_contable: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Dirección</label>
                <input
                  type="text"
                  value={form.direccion ?? ""}
                  onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Notas</label>
                <textarea
                  rows={3}
                  value={form.notas ?? ""}
                  onChange={(e) => setForm({ ...form, notas: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createMut.isPending || updateMut.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
              >
                <Save size={14} /> Guardar
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={confirmDel !== null}
        title="Eliminar cliente"
        message={confirmDel ? `¿Eliminar a "${confirmDel.nombre}"? Esta acción no se puede deshacer.` : ""}
        confirmLabel="Eliminar"
        destructive
        onConfirm={() => {
          if (confirmDel) deleteMut.mutate(confirmDel.id);
        }}
        onClose={() => setConfirmDel(null)}
      />
    </div>
  );
}
